import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import type { Task } from '@/api/types';
import { Icon } from '@/components/Icon';
import { dueLabel } from '@/features/tasks/due';
import { todayCalendarDay } from '@/utils/format';
import { rfcMessageIdOf } from './emailTask';
import './email-task.css';

/**
 * "In Tasks · due Tomorrow, 9:00 AM" under the subject of an open message
 * that a task was made from (ADR 0075). One indexed read by the message's
 * Message-ID (and its id, for mail with none); nothing is drawn while it
 * loads, when it fails, or when there is no such task — it is a hint, not a
 * control that must always be there.
 *
 * The open task it names is the first open one; when every task from this
 * email is done, it says so.
 */
export function EmailTaskChip({
  accountId,
  messageId,
  headers,
  onOpen,
}: {
  accountId: string;
  messageId: string;
  headers: ReadonlyArray<{ name: string; value: string }> | undefined;
  onOpen: (task: Task) => void;
}) {
  const api = useApi();
  const rfcMessageId = rfcMessageIdOf(headers) ?? '';
  const query = useQuery({
    queryKey: queryKeys.account(accountId).tasksByEmail(rfcMessageId, messageId),
    queryFn: ({ signal }) =>
      api.tasks.byEmail(accountId, { ...(rfcMessageId ? { rfcMessageId } : {}), messageId }, signal),
    enabled: accountId !== '' && messageId !== '',
    // A hint beside the message: a failed read is not worth an error, or a retry storm.
    retry: false,
  });
  const tasks = query.data ?? [];
  if (tasks.length === 0) return null;
  const open = tasks.find((task) => task.completedAt === null);
  const task = open ?? tasks[0]!;
  const today = todayCalendarDay();
  const due = open?.dueDate ? dueLabel(open.dueDate, open.dueTime, today) : undefined;
  const text = open ? (due ? `In Tasks · due ${due.text}` : 'In Tasks') : 'Done in Tasks';
  const spoken = open
    ? `In Tasks as “${task.title}”${due ? `, ${due.spoken.charAt(0).toLowerCase()}${due.spoken.slice(1)}` : ''}. Open the task`
    : `Done in Tasks as “${task.title}”. Open the task`;
  return (
    <button
      type="button"
      className="chip email-task-chip"
      data-tone={due?.tone ?? (open ? 'none' : 'done')}
      aria-label={spoken}
      title={task.title}
      onClick={() => onOpen(task)}
    >
      <Icon name={open ? 'tasks' : 'check'} size={14} />
      <span>{text}</span>
    </button>
  );
}
